/**
 * PageLayoutGrid
 *
 * Core layout system providing a flexible grid with optional left sidebar,
 * right sidebar, and bottom drawer. The grid container has NO padding -
 * each page controls its own content spacing.
 *
 * Usage:
 * ```tsx
 * <PageLayoutGrid
 *   leftSidebar={<CandidateList />}
 *   rightSidebar={<BondSeriesEditor />}
 *   bottomDrawer={<DataDrawer />}
 * >
 *   <YourPageContent />
 * </PageLayoutGrid>
 * ```
 */

import { Box, Grid, GridItem } from '@chakra-ui/react';
import type React from 'react';

export interface PageLayoutGridProps {
  /** Optional left sidebar (collapsible) */
  leftSidebar?: React.ReactNode;

  /** Optional right sidebar (collapsible) */
  rightSidebar?: React.ReactNode;

  /** Optional bottom drawer (slides up from bottom) */
  bottomDrawer?: React.ReactNode;

  /** Main page content */
  children: React.ReactNode;

  /** Background color for the entire layout */
  bg?: string;

  /** Height for the layout (default: 100% to respect parent container) */
  height?: string;

  /** Optional className for styling */
  className?: string;
}

/**
 * PageLayoutGrid Component
 *
 * Features:
 * - Zero forced padding - pages control their own spacing
 * - Responsive grid that stacks on mobile
 * - Supports any combination of left/right sidebars
 * - Bottom drawer sits outside grid in fixed position
 * - Grid automatically adjusts columns based on which sidebars are present
 * - Default height="100%" respects parent container (PageContainer accounts for header)
 */
export const PageLayoutGrid: React.FC<PageLayoutGridProps> = ({
  leftSidebar,
  rightSidebar,
  bottomDrawer,
  children,
  bg = 'gray.50',
  height = '100%',
  className,
}) => {
  // Determine grid template based on which sidebars are present
  const getGridTemplate = () => {
    const hasLeft = !!leftSidebar;
    const hasRight = !!rightSidebar;

    // Desktop: adjust columns based on sidebars
    // Mobile: single column
    if (hasLeft && hasRight) {
      return {
        base: '1fr', // Mobile: single column
        md: 'auto 1fr auto', // Desktop: left + center + right
      };
    } else if (hasLeft) {
      return {
        base: '1fr', // Mobile: single column
        md: 'auto 1fr', // Desktop: left + center
      };
    } else if (hasRight) {
      return {
        base: '1fr', // Mobile: single column
        md: '1fr auto', // Desktop: center + right
      };
    } else {
      return '1fr'; // No sidebars: single column
    }
  };

  // Calculate columns that exist (for proper GridItem placement)
  const hasLeft = !!leftSidebar;
  const hasRight = !!rightSidebar;

  return (
    <Box
      width="100%"
      height={height}
      maxHeight={height}
      bg={bg}
      position="relative"
      className={className}
      overflow="hidden"
    >
      {/* Main Grid Layout */}
      <Grid
        templateColumns={getGridTemplate()}
        gap={0} // No gap - sidebars have their own borders
        width="100%"
        height="100%"
      >
        {/* Left Sidebar (if present) */}
        {hasLeft && (
          <GridItem height="100%" overflow="hidden">
            {leftSidebar}
          </GridItem>
        )}

        {/* Main Content Area */}
        <GridItem height="100%" overflow="auto" data-main-content>
          {children}
        </GridItem>

        {/* Right Sidebar (if present) */}
        {hasRight && (
          <GridItem height="100%" overflow="hidden">
            {rightSidebar}
          </GridItem>
        )}
      </Grid>

      {/* Bottom Drawer (fixed position, outside grid) */}
      {bottomDrawer && <>{bottomDrawer}</>}
    </Box>
  );
};
